import { gql } from "@apollo/client";
import { Button, Dialog } from "@mui/material";
import CsvPreviewTable, { csvRequiredColumnsMessage, csvToAssetUploader, parseCsv } from "controls/CsvPreviewTable";
import LinkButton from "controls/LinkButton";
import Message from "controls/Message";
import getUiString from "helpers/getUiString";
import { useActionFragment } from "hooks/useAction";
import useData from "hooks/useData";
import SwitchInput from "inputs/SwitchInput";
import UploaderInput from "inputs/UploaderInput";
import { FileUpload } from "mdi-material-ui";
import React, { useState } from "react";

// Relaxed required columns: email columns are optional. If no email provided, coaches are imported as stubbed accounts.
export const COACH_CSV_REQUIRED_HEADER_GROUPS = [["Team Name"], ["Head Coach Name", "Head Coach"]];

// Full set of acceptable columns. Competition Name and the Assistant Coach columns are optional
export const COACH_CSV_TEMPLATE_HEADERS = [
  "Competition Name",
  "Team Name",
  "Head Coach Name",
  "Head Coach Email",
  "Assistant Coach Name",
  "Assistant Coach Email",
];

export function downloadCoachCsvTemplate() {
  const exampleRow = [
    "Example Competition",
    "Team A",
    "John Smith",
    "john@example.com",
    "Jane Doe",
    "jane@example.com",
  ];
  const csv = [COACH_CSV_TEMPLATE_HEADERS.join(","), exampleRow.join(",")].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "coach_import_template.csv";
  a.click();
  URL.revokeObjectURL(url);
}

const PREVIEW_MAX_ROWS = 500;

const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

const requiredNonEmpty = (v) => (!v.trim() ? "Cannot be empty" : null);
// Email columns are optional; stubbed accounts are created for coaches without emails.
const optionalEmail = (v) => (v.trim() && !isValidEmail(v) ? "Must be a valid email address" : null);

const COLUMN_VALIDATORS = {
  "Team Name": requiredNonEmpty,
  "Head Coach Name": requiredNonEmpty,
  "Head Coach": requiredNonEmpty,
  "Head Coach Email": optionalEmail,
  "Assistant Coach Email": optionalEmail,
};

ImportCoachReportDialog.useButtonProps = ({ orgId }) => {
  if (!orgId) return null;
  return { icon: <FileUpload />, content: "Import Coaches CSV" };
};

export default function ImportCoachReportDialog({ orgId, onSubmit, onClose }) {
  const [phase, setPhase] = useState("upload"); // 'upload' | 'done'
  const [uploaderValue, setUploaderValue] = useState(null);
  const [csvHeaders, setCsvHeaders] = useState(null);
  const [csvRows, setCsvRows] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [autoEnableTeamRegistration, setAutoEnableTeamRegistration] = useState(false);

  const [orgData] = useData(
    gql`
      query ImportCoachReportDialog($orgId: ID!) {
        org(id: $orgId) {
          id
          isApiIntegrated
        }
      }
    `,
    { orgId },
  );
  const isApiIntegrated = orgData?.org.isApiIntegrated;

  const importReport = useActionFragment("importCoachReport", `report { id imported importErrorMessage }`);

  const handleUploaderChange = async (value) => {
    setUploaderValue(value);
    setCsvHeaders(null);
    setCsvRows(null);
    setErrorMessage(null);
    if (!value?.blobUrl) return;
    try {
      const text = await fetch(value.blobUrl).then((r) => r.text());
      const { headers, rows } = parseCsv(text);
      setCsvHeaders(headers);
      setCsvRows(rows);
    } catch (e) {
      setErrorMessage(`Could not read CSV file: ${e.message}`);
    }
  };

  const presentHeaders = new Set((csvHeaders ?? []).map((h) => h.trim()));
  const missingHeaderGroups = csvHeaders
    ? COACH_CSV_REQUIRED_HEADER_GROUPS.filter((group) => !group.some((h) => presentHeaders.has(h)))
    : [];
  const headerError = missingHeaderGroups.length
    ? `This file is missing required column(s): ${missingHeaderGroups
        .map((group) => group.join(" or "))
        .join(", ")}. Please upload a CSV that matches the template.`
    : null;

  // Large files skip the client-side preview and validation entirely; the backend will still validate every row.
  const previewHidden = csvRows && csvRows.length > PREVIEW_MAX_ROWS;

  // Shown validation errors as warnings only
  const hasValidationWarnings =
    !previewHidden &&
    (csvRows?.some((row) => csvHeaders?.some((h, i) => COLUMN_VALIDATORS[h]?.(row[i] ?? "") != null)) ?? false);

  const canSubmit = csvHeaders && csvRows && csvRows.length > 0 && !headerError && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const assetUploader = csvToAssetUploader(csvHeaders, csvRows, uploaderValue.name);

      const result = await importReport(
        { input: { orgId, assetUploader, autoEnableTeamRegistration } },
        { suppressError: true },
      );
      await onSubmit?.(result.report);
      setPhase("done");
    } catch (error) {
      setErrorMessage(error.message.replace("GraphQL error: ", ""));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open
      maxWidth="lg"
      fullWidth
      scroll="body"
      onClose={(_, reason) => {
        if (reason === "backdropClick") return;
        onClose?.();
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: 16 }}>
        <div style={{ fontSize: "1.2em", fontWeight: "bold" }}>Upload Coach CSV</div>

        {phase === "upload" && (
          <>
            <Message
              type="info"
              content={
                <>
                  {csvRequiredColumnsMessage(COACH_CSV_REQUIRED_HEADER_GROUPS.map((group) => group[0]))} <br />
                  Optional columns: Competition Name, Head Coach Email, Assistant Coach Name and Assistant Coach Email.{" "}
                  <br />
                  You can <LinkButton onClick={downloadCoachCsvTemplate}>download</LinkButton> the template CSV.
                </>
              }
            />

            {errorMessage && <Message type="error" content={errorMessage} />}

            <UploaderInput
              name="assetUploader"
              label="CSV Upload"
              value={uploaderValue}
              onChange={handleUploaderChange}
              accept=".csv"
            />

            {headerError && <Message type="error" content={headerError} />}

            {hasValidationWarnings && (
              <Message
                type="warning"
                content="Some rows have invalid or missing values (highlighted in yellow). You can still import, and you'll be emailed a report of any that couldn't be processed."
              />
            )}

            {csvHeaders &&
              csvRows &&
              (previewHidden ? (
                <Message
                  type="info"
                  content={`Preview unavailable — file has ${csvRows.length.toLocaleString()} rows. Every row will still be validated during import, and you'll be emailed a report of any that couldn't be processed.`}
                />
              ) : (
                <CsvPreviewTable
                  headers={csvHeaders}
                  rows={csvRows}
                  onChange={setCsvRows}
                  columnValidators={COLUMN_VALIDATORS}
                  validationsAsWarnings
                />
              ))}

            {!isApiIntegrated && (
              <SwitchInput
                value={autoEnableTeamRegistration}
                onChange={setAutoEnableTeamRegistration}
                label={getUiString("readmin.org.import_coach_report.auto_enable_team_registration.label")}
                helperText={getUiString("readmin.org.import_coach_report.auto_enable_team_registration.helper")}
              />
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 4 }}>
              <Button variant="contained" color="primary" onClick={handleSubmit} disabled={!canSubmit}>
                {submitting ? "Uploading…" : "Confirm & Import"}
              </Button>
              <Button onClick={onClose} color="inherit">
                Cancel
              </Button>
            </div>
          </>
        )}

        {phase === "done" && (
          <>
            <Message
              type="success"
              content="The CSV file has been uploaded successfully. The system will continue processing the file in the background."
            />
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button variant="contained" onClick={onClose}>
                Done
              </Button>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}
